import { useEffect } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/config/queryConfig'

/**
 * #392 (review R3) — the safety net of the critical history. The Events lists and Favourites do not poll, so a
 * missed `critical_acknowledged` WebSocket message would leave a card "Not acknowledged yet", and a new critical
 * event would not reach the Critical tab. The open critical list (the alert bar) polls every 30 s: whenever the ids
 * it holds change (one acknowledged anywhere left it, a new one arrived), the Critical tab
 * (`['events', 'paginated', {critical: 'all', …}]`) and Favourites are refreshed too.
 *
 * The ids are read from the list as the server (or the WebSocket placeholder) gave it, not from the open-only
 * selection: an acknowledgement made in this tab marks the rows at once (optimistic) without removing them, so that
 * refreshes nothing; the refetch after the POST drops the row, and the history is refreshed then, with the server
 * already knowing. Mount ONCE per tab: the critical alerts controller (CriticalAlertsProvider) does.
 */
export function useCriticalHistoryRefresh() {
  const queryClient = useQueryClient()
  useEffect(() => {
    const openKey = queryKeys.events.criticalOpen()
    let last = openIds(queryClient)
    return queryClient.getQueryCache().subscribe((cacheEvent) => {
      if (cacheEvent.type !== 'updated' || !sameKey(cacheEvent.query.queryKey, openKey)) return
      const now = openIds(queryClient)
      if (now === null) return
      if (last !== null && now !== last) refreshCriticalHistory(queryClient)
      last = now
    })
  }, [queryClient])
}

/** The Critical tab's lists (any other filters) and Favourites. */
export function refreshCriticalHistory(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    queryKey: ['events', 'paginated'],
    predicate: (q) => (q.queryKey[2] as { critical?: unknown } | undefined)?.critical === 'all',
  })
  queryClient.invalidateQueries({ queryKey: ['events', 'favourites'] })
}

/** The ids in the cached open list, sorted (a new order is not a change); null while it has no data. */
function openIds(queryClient: QueryClient): string | null {
  const data = queryClient.getQueryData(queryKeys.events.criticalOpen()) as { results?: unknown } | undefined
  if (!data || !Array.isArray(data.results)) return null
  return data.results
    .map((e) => (e as { id?: unknown }).id)
    .filter((id): id is number => typeof id === 'number')
    .sort((a, b) => a - b)
    .join(',')
}

function sameKey(a: readonly unknown[], b: readonly unknown[]): boolean {
  return a.length === b.length && a.every((part, i) => part === b[i])
}
